import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Paper,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

import { fetchMyPurchaseOrders } from '../actions/customerActions';
import { getPaymentLink, isPaid } from '../lib/payments';
import { formatPrice } from '../lib/money';
import {
    deliveryAddressLabel,
    isHomeDelivery,
    orderTotal,
    productItems,
    shippingCost,
} from '../lib/orders';

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleDateString('es-AR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
          })
        : '';

// Mismo intervalo que el poll de la call request (callRequestActions).
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_TICKS = 24;

const MyPurchaseOrders = ({
    purchaseOrders,
    isFetching,
    fetchMyPurchaseOrders,
}) => {
    const navigate = useNavigate();

    useEffect(() => {
        fetchMyPurchaseOrders();
    }, [fetchMyPurchaseOrders]);

    // Un pago online se confirma después de que el comprador vuelve del sitio
    // del proveedor, así que la pantalla montada con la orden en "pendiente"
    // se quedaba mintiendo hasta que alguien recargaba. Poll acotado: sólo
    // mientras haya algo pendiente que pagar, y por dos minutos como mucho.
    const ticksLeft = useRef(POLL_MAX_TICKS);

    useEffect(() => {
        const pending = (purchaseOrders || []).some(
            (purchaseOrder) =>
                !isPaid(purchaseOrder) && getPaymentLink(purchaseOrder)
        );

        if (!pending || ticksLeft.current <= 0) return undefined;

        const interval = setInterval(() => {
            ticksLeft.current -= 1;
            if (ticksLeft.current <= 0) {
                clearInterval(interval);
                return;
            }
            fetchMyPurchaseOrders();
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [purchaseOrders, fetchMyPurchaseOrders]);

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={() => navigate('/account')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" component="h1">
                    Mis órdenes de compra
                </Typography>
            </Box>

            {isFetching ? (
                <Paper elevation={3}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 6,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                </Paper>
            ) : purchaseOrders.length === 0 ? (
                <Paper elevation={3}>
                    <Typography
                        align="center"
                        color="text.secondary"
                        sx={{ py: 6, px: 2 }}
                    >
                        Todavía no tenés órdenes de compra.
                    </Typography>
                </Paper>
            ) : (
                purchaseOrders.map((purchaseOrder) => {
                    const paid = isPaid(purchaseOrder);
                    const initPoint = getPaymentLink(purchaseOrder);
                    const total = orderTotal(purchaseOrder);
                    const items = productItems(purchaseOrder);
                    const shipping = shippingCost(purchaseOrder);
                    const homeDelivery = isHomeDelivery(purchaseOrder);
                    const deliveryAddress = homeDelivery
                        ? deliveryAddressLabel(purchaseOrder)
                        : '';

                    return (
                        <Paper
                            key={purchaseOrder.purchaseOrderId}
                            elevation={3}
                            sx={{ p: 2, mb: 2 }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 1,
                                }}
                            >
                                <Avatar
                                    src={
                                        purchaseOrder.storeImageUrl || undefined
                                    }
                                >
                                    {purchaseOrder.storeName
                                        ? purchaseOrder.storeName[0]
                                        : '?'}
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography>
                                        {purchaseOrder.storeName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {formatDate(purchaseOrder.createdOn)}
                                        {' · '}
                                        {items.length} producto(s)
                                    </Typography>
                                </Box>
                                <Chip
                                    label={
                                        paid
                                            ? 'Pagada'
                                            : purchaseOrder.paymentStatus ||
                                              'Pendiente de pago'
                                    }
                                    color={paid ? 'success' : 'warning'}
                                    size="small"
                                />
                            </Box>

                            {items.map((item, index) => (
                                <Box
                                    key={item.purchaseOrderItemId || index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 0.5,
                                    }}
                                >
                                    {item.imageUrl && (
                                        <Avatar
                                            variant="rounded"
                                            src={item.imageUrl}
                                            alt={item.productName}
                                            sx={{ width: 40, height: 40 }}
                                        />
                                    )}
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {item.quantity} x {item.productName}{' '}
                                            — {formatPrice(item.unitPrice)} c/u
                                        </Typography>
                                        {item.variantLabel && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {item.variantLabel}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}

                            {shipping !== null && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        py: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Envío
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {formatPrice(shipping)}
                                    </Typography>
                                </Box>
                            )}

                            {homeDelivery && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2">
                                        Envío a domicilio (Correo Argentino)
                                    </Typography>
                                    {deliveryAddress && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {deliveryAddress}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mt: 1.5,
                                }}
                            >
                                <Typography sx={{ fontWeight: 600 }}>
                                    Total: {formatPrice(total)}
                                </Typography>
                                {!paid && initPoint && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        href={initPoint}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Pagar
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                    );
                })
            )}
        </Container>
    );
};

const mapStateToProps = (state) => {
    return {
        purchaseOrders: state.customer.purchaseOrders,
        isFetching: state.customer.isFetchingPurchaseOrders,
    };
};

export default connect(mapStateToProps, { fetchMyPurchaseOrders })(
    MyPurchaseOrders
);
